import { KNOWLEDGE_BASE_AGENT_ID } from "@repo/app-types/entities/configurationAgent";
import type { CopilotProposalId } from "@repo/app-types/entities/copilotProposal";
import type { KnowledgeBaseAttachment } from "@repo/app-types/entities/knowledgeBase";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { Label } from "@repo/ui/Label/label";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { IconChevronLeft } from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";
import { useResolveProposalMutation } from "../../../api/entities/copilotProposal.js";

import { useDeleteKnowledgeBaseAttachmentMutation } from "../../../api/entities/knowledgeBase.js";
import { isConflictQueryError } from "../../../api/queryErrors.js";
import { ResourceSaveFooter } from "../../../components/resourceSaveFooter.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard.js";
import { AttachmentEditorContent } from "../../agents/components/attachmentEditorContent.js";
import { useAttachmentEditorState } from "../../agents/components/useAttachmentEditorState.js";
import { MastraChatProvider } from "../../agents/mastra/mastraChatContext.js";
import { useMastraChat } from "../../agents/mastra/useMastraChat.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { useDeepLinkedProposal } from "../../researchAgent/copilotDeepLinks.js";
import { useReportEditorProposal } from "../../researchAgent/editorProposalsSlice.js";
import { KnowledgeBaseAssistantChat } from "./knowledgeBaseAssistant/knowledgeBaseAssistantChat.js";
import { KnowledgeBaseAssistantContextProvider } from "./knowledgeBaseAssistant/knowledgeBaseAssistantContext.js";
import {
  buildInitialAssistantMessage,
  usePrefillFromUrl,
} from "./knowledgeBaseAssistant/knowledgeBaseAssistantPrefill.js";
import { KnowledgeBaseSyncPill } from "./knowledgeBaseSyncPill.js";

/**
 * A find/replace edit waiting for the document body to load: from the page's
 * assistant, or from an Ask Alpharun proposal, which is recorded as applied
 * once the body carrying the edit is saved.
 */
interface PendingPatch {
  find: string;
  replace: string;
  proposalId?: CopilotProposalId;
}

interface KnowledgeBaseAttachmentEditorProps {
  knowledgeBaseId: string;
  attachmentId: string;
  /** All attachments in the knowledge base, for document name resolution */
  knowledgeBaseAttachments?: readonly KnowledgeBaseAttachment[];
  /** Known read-only state from the parent knowledge base while the document loads. */
  isReadOnly?: boolean;
  onClose: () => void;
}

export const KnowledgeBaseAttachmentEditor: FC<
  KnowledgeBaseAttachmentEditorProps
> = (props) => {
  const {
    knowledgeBaseId,
    attachmentId,
    knowledgeBaseAttachments,
    isReadOnly,
    onClose,
  } = props;
  const dispatch = useAppDispatch();

  // Pre-fill from URL params (when redirected from a finding feedback)
  const prefill = usePrefillFromUrl();
  const initialMessages = useMemo(
    () => (prefill ? [buildInitialAssistantMessage(prefill)] : undefined),
    [prefill],
  );

  const chat = useMastraChat({
    agentId: KNOWLEDGE_BASE_AGENT_ID,
    initialMessages,
    onSend: async () => ({
      requestContext: {
        knowledgeBaseId,
        currentAttachmentId: attachmentId,
      },
    }),
  });

  const {
    attachment,
    isLoading,
    isSaving,
    isInitialized,
    editedBody,
    setEditedBody,
    fetchedBody,
    hasUnsavedChanges,
    onSave,
    onDiscard,
    selectedTabIndex,
    setSelectedTabIndex,
  } = useAttachmentEditorState({ knowledgeBaseId, attachmentId });
  const { guardModal, navigateWithoutConfirmation } =
    useUnsavedChangesGuard(hasUnsavedChanges);

  const [pendingPatch, setPendingPatch] = useState<PendingPatch | null>(null);
  // The proposal whose edit is in the unsaved body.
  const [linkedProposalId, setLinkedProposalId] = useState<
    CopilotProposalId | undefined
  >();
  const [resolveProposal] = useResolveProposalMutation();

  const notifyProposalUnavailable = useCallback(() => {
    showNotification(dispatch, {
      id: `attachment-proposal-unavailable-${Date.now().toString()}`,
      type: NotificationType.FAILURE,
      primaryMessage: "This suggestion is no longer available.",
    });
  }, [dispatch]);
  // One-shot link from an Ask Alpharun row (?proposalId=...): the pending
  // row's edit goes into the body as soon as both have loaded. A row for
  // another document loads nothing.
  const { row: deepLinkProposal, clear: clearDeepLinkProposalId } =
    useDeepLinkedProposal("knowledgeBaseAttachment", {
      onUnavailable: notifyProposalUnavailable,
    });
  useEffect(() => {
    if (!deepLinkProposal) {
      return;
    }
    clearDeepLinkProposalId();
    if (deepLinkProposal.resource_id === attachmentId) {
      setPendingPatch({
        find: deepLinkProposal.data.find,
        replace: deepLinkProposal.data.replace,
        proposalId: deepLinkProposal.id,
      });
    }
  }, [deepLinkProposal, attachmentId, clearDeepLinkProposalId]);

  // The chat marks the row this editor holds, and reads Saving while it saves.
  useReportEditorProposal(
    pendingPatch?.proposalId ?? linkedProposalId,
    isSaving,
  );

  // Apply the pending patch once the body is loaded. A proposal's edit then
  // stays linked until Save records it as applied. Text the proposal expected
  // to find but that is no longer in the body leaves the proposal pending.
  const appliedPatchRef = useRef<PendingPatch | null>(null);
  useEffect(() => {
    if (
      !pendingPatch ||
      !isInitialized ||
      appliedPatchRef.current === pendingPatch
    ) {
      return;
    }
    appliedPatchRef.current = pendingPatch;
    setPendingPatch(null);

    const idx = editedBody.indexOf(pendingPatch.find);
    if (idx === -1) {
      if (pendingPatch.proposalId) {
        showNotification(dispatch, {
          id: `attachment-proposal-missing-${Date.now().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage: "This text is no longer in the document",
          secondaryMessage: "The suggested edit could not be applied.",
        });
      }
      return;
    }
    setEditedBody(
      editedBody.substring(0, idx) +
        pendingPatch.replace +
        editedBody.substring(idx + pendingPatch.find.length),
    );
    setSelectedTabIndex(1); // Switch to "Diff" tab
    if (pendingPatch.proposalId) {
      setLinkedProposalId(pendingPatch.proposalId);
    }
  }, [
    pendingPatch,
    isInitialized,
    editedBody,
    setEditedBody,
    setSelectedTabIndex,
    dispatch,
  ]);

  const handleApplyPatch = useCallback(
    (patch: { find: string; replace: string }) => {
      setPendingPatch(patch);
    },
    [],
  );

  const [deleteAttachment, { isLoading: isDeleting }] =
    useDeleteKnowledgeBaseAttachmentMutation();

  const handleSave = useCallback(async () => {
    try {
      await onSave();
      showNotification(dispatch, {
        id: `attachment-save-${Date.now().toString()}`,
        type: NotificationType.SUCCESS,
        primaryMessage: "Article saved",
        secondaryMessage: "Your changes have been saved.",
      });
    } catch {
      showNotification(dispatch, {
        id: `attachment-save-error-${Date.now().toString()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to save article",
        secondaryMessage: "Please try again.",
      });
      return;
    }
    // The saved body carried the suggestion, so it is applied. One that has
    // moved on (409) was already decided; the save stands either way. Any other
    // failure keeps the suggestion linked, so the next Save retries it.
    if (!linkedProposalId) {
      return;
    }
    try {
      await resolveProposal({
        proposalId: linkedProposalId,
        status: "applied",
      }).unwrap();
    } catch (err) {
      if (!isConflictQueryError(err)) {
        showNotification(dispatch, {
          id: `attachment-proposal-resolve-error-${Date.now().toString()}`,
          type: NotificationType.FAILURE,
          primaryMessage:
            "The article was saved, but the suggestion could not be marked as applied.",
        });
        return;
      }
      notifyProposalUnavailable();
    }
    setLinkedProposalId(undefined);
  }, [
    onSave,
    dispatch,
    linkedProposalId,
    resolveProposal,
    notifyProposalUnavailable,
  ]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteAttachment({ knowledgeBaseId, attachmentId }).unwrap();
      showNotification(dispatch, {
        id: `delete-success-${attachmentId}`,
        primaryMessage: `Successfully deleted "${attachment?.title || attachment?.file_name || ""}" document`,
        type: NotificationType.SUCCESS,
      });
      navigateWithoutConfirmation(onClose);
    } catch (err: any) {
      showNotification(dispatch, {
        id: `delete-error-${attachmentId}`,
        primaryMessage: `Failed to delete document: ${err.data?.error || err.message || "Unknown error"}`,
        type: NotificationType.FAILURE,
      });
    }
  }, [
    deleteAttachment,
    knowledgeBaseId,
    attachmentId,
    attachment,
    dispatch,
    navigateWithoutConfirmation,
    onClose,
  ]);

  const {
    renderConfirmationModal: renderDeleteModal,
    onConfirmThenClose: onConfirmThenDelete,
  } = useConfirmationModal({
    needsConfirmation: true,
    onClose: handleDelete,
  });

  // Keep the state unknown until either the document or its parent KB has
  // loaded. Treating undefined as editable flashes the AI assistant while a
  // synced document is in flight.
  const isSynced = attachment ? Boolean(attachment.sync) : isReadOnly;
  const documentTitle = attachment
    ? attachment.title || attachment.file_name || "Untitled document"
    : undefined;
  return (
    <div className="flex h-full flex-col">
      {guardModal}
      {renderDeleteModal({
        title: "Delete document?",
        message:
          "Are you sure you want to delete this document? This action cannot be undone.",
        confirmText: "Delete",
      })}
      <PageHeader
        className="pb-3"
        title={documentTitle}
        isLoading={!documentTitle}
        leadingAction={
          <IconButton
            accessibleLabel="Back to documents"
            variant={ButtonVariant.Tertiary}
            onClick={onClose}
            size={Size.SMALL}
            icon={<IconChevronLeft aria-hidden className="size-4" />}
          />
        }
        actions={
          attachment?.sync ? (
            <KnowledgeBaseSyncPill
              provider={attachment.sync.provider}
              lastSyncedAt={attachment.sync.synced_at}
            />
          ) : isSynced === false ? (
            <Button
              variant={ButtonVariant.Warning}
              onClick={onConfirmThenDelete}
              isDisabled={isDeleting}
              size={Size.SMALL}
            >
              Delete
            </Button>
          ) : undefined
        }
      />
      <div className="flex min-h-0 flex-1">
        <AttachmentEditorContent
          attachment={attachment}
          isLoading={isLoading}
          isSaving={isSaving}
          isReadOnly={isSynced}
          showTitle={false}
          editedBody={editedBody}
          savedBody={fetchedBody}
          setEditedBody={setEditedBody}
          selectedTabIndex={selectedTabIndex}
          onSelectedTabIndexChange={setSelectedTabIndex}
        />
        {isSynced === false ? (
          <MastraChatProvider chat={chat}>
            <KnowledgeBaseAssistantContextProvider
              onApplyPatch={handleApplyPatch}
              knowledgeBaseAttachments={knowledgeBaseAttachments}
            >
              <div className="flex h-full w-[380px] flex-col border-l border-border-light bg-background">
                <div className="border-b border-border-light bg-white px-4 py-3">
                  <Label size={Size.SMALL}>AI Assistant</Label>
                </div>
                <div className="flex min-h-0 flex-1 flex-col">
                  <KnowledgeBaseAssistantChat />
                </div>
              </div>
            </KnowledgeBaseAssistantContextProvider>
          </MastraChatProvider>
        ) : null}
      </div>
      {isSynced === false && (hasUnsavedChanges || linkedProposalId) ? (
        <ResourceSaveFooter
          onCancel={() => {
            onDiscard();
            setLinkedProposalId(undefined);
          }}
          onSave={() => {
            void handleSave();
          }}
          isSaving={isSaving}
        />
      ) : null}
    </div>
  );
};
